import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp,
  ClipboardCheck,
  LayoutDashboard,
  Search,
  Settings,
  Bookmark,
  UserCheck,
  LogOut,
  Menu,
  X,
  PenTool,
  UserCog,
  Send,
  ChevronRight,
  ChevronDown,
  FlaskConical,
  Crown,
  Newspaper,
  BarChart2,
  LockKeyhole,
  Zap,
  Infinity,
  Clock,
  Timer,
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { useState } from 'react';
import PackageModal, { PackageBadge } from './PackageModal';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const isAdmin      = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';
  const currentPkg   = user?.package || 'free';
  const isPlatinum   = currentPkg === 'platinum';

  const daysLeft = (() => {
    const end = user?.subscription_end_date;
    if (!end || currentPkg === 'free') return null;
    return Math.ceil((new Date(end) - new Date()) / (1000 * 60 * 60 * 24));
  })();

  const navItems = [
    { path: '/dashboard',   label: 'Dashboard',         icon: LayoutDashboard },
    { path: '/analysis',    label: 'Benzerlik Analizi', icon: Search },
    ...(isAdmin
      ? [{
          group: 'patterns',
          label: 'Kalıp Araçları',
          icon: PenTool,
          children: [
            { path: '/draw-pattern',   label: 'Kalıp Çizimi', icon: PenTool },
            { path: '/custom-pattern', label: 'Özel Kalıp',   icon: Settings },
          ],
        }]
      : []),
    { path: '/saved',         label: 'Kayıtlı Analizler', icon: Bookmark },
    {
      group: 'platinum',
      label: 'Platinum Analizler',
      icon: Crown,
      children: [
        { path: '/bist-analizi',   label: 'BIST Analizi',   icon: BarChart2, locked: !isPlatinum && !isAdmin },
        { path: '/simons-analizi', label: 'Simons Analizi', icon: Crown,     locked: !isPlatinum && !isAdmin },
      ],
    },
  ];

  // Admin araştırma araçları tek grupta
  const labItems = [
    { path: '/admin/hourly-analysis',  label: 'Saatlik Analiz', icon: Timer,          testId: 'nav-hourly-analysis' },
    { path: '/admin/simons-score',     label: 'Simons Skoru',   icon: TrendingUp,     testId: 'nav-simons-score' },
    { path: '/admin/signal-scorecard', label: 'Sinyal Karnesi', icon: ClipboardCheck, testId: 'nav-signal-scorecard' },
  ];

  const isActive = (path) => location.pathname === path;
  const groupHasActive = (children) => children.some((c) => isActive(c.path));

  // Kullanıcının açıp kapattığı gruplar; içindeki sayfadaysan grup kendiliğinden açık gelir
  const [openGroups, setOpenGroups] = useState({});
  const isGroupOpen = (key, children) => openGroups[key] ?? groupHasActive(children);
  const toggleGroup = (key, children) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? groupHasActive(children)) }));

  const openUpgrade = () => {
    setMobileOpen(false);
    setUpgradeModalOpen(true);
  };

  const renderLink = (item, nested = false) =>
    item.locked ? (
      <button
        key={item.path}
        onClick={openUpgrade}
        className={`sidebar-link w-full text-left ${nested ? '!pl-9 !py-2' : ''}`}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        <LockKeyhole className="w-3.5 h-3.5 ml-auto flex-shrink-0 text-[#C86F4A]" />
      </button>
    ) : (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`sidebar-link ${nested ? '!pl-9 !py-2' : ''} ${isActive(item.path) ? 'active' : ''}`}
        data-testid={item.testId || `nav-${item.path.slice(1)}`}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
      </Link>
    );

  const renderGroup = (key, label, Icon, children) => {
    const open = isGroupOpen(key, children);
    return (
      <div key={key}>
        <button
          onClick={() => toggleGroup(key, children)}
          className={`sidebar-link w-full text-left ${!open && groupHasActive(children) ? 'active' : ''}`}
          data-testid={`nav-group-${key}`}
        >
          <Icon className="w-5 h-5" />
          <span>{label}</span>
          {open
            ? <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />}
        </button>
        {open && <div className="mt-1 space-y-1">{children.map((c) => renderLink(c, true))}</div>}
      </div>
    );
  };

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-6 border-b border-[#E6DCCF]">
        <div className="w-10 h-10 rounded-full bg-[#C86F4A] flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <span className="text-xl font-bold text-[#2E2620] font-['Playfair_Display']">
          BIST Analiz
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto flex flex-col py-4 px-3 space-y-1">
        {navItems.map((item) => (item.children ? renderGroup(item.group, item.label, item.icon, item.children) : renderLink(item)))}

        {/* Admin-only links + Telegram — her zaman en altta */}
        <div className="mt-auto pt-4 space-y-1">
          {isAdmin && (
            <>
              <Link
                to="/admin/bulletin"
                onClick={() => setMobileOpen(false)}
                className={`sidebar-link ${isActive('/admin/bulletin') ? 'active' : ''}`}
                data-testid="nav-bulletin"
              >
                <Newspaper className="w-5 h-5" />
                <span>Bülten Yönetimi</span>
              </Link>
              {renderGroup('lab', 'Simons Laboratuvarı', FlaskConical, labItems)}
              {!isSuperAdmin && (
                <Link
                  to="/admin/pending-approvals"
                  onClick={() => setMobileOpen(false)}
                  className={`sidebar-link ${isActive('/admin/pending-approvals') ? 'active' : ''}`}
                  data-testid="nav-pending-approvals"
                >
                  <UserCheck className="w-5 h-5" />
                  <span>Onay Bekleyenler</span>
                </Link>
              )}
              {isSuperAdmin && (
                <Link
                  to="/admin/users"
                  onClick={() => setMobileOpen(false)}
                  className={`sidebar-link ${isActive('/admin/users') ? 'active' : ''}`}
                  data-testid="nav-super-admin-users"
                >
                  <Settings className="w-5 h-5" />
                  <span>Süper Admin Paneli</span>
                </Link>
              )}
            </>
          )}

          <a
            href="https://t.me/emrekirbayir?text=Merhaba%2C%20teknik%20destek%20ve%20kullanim%20ile%20ilgili%20yardim%20istiyorum."
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-1 w-full flex items-start gap-2 rounded-md px-3 py-2 text-left text-xs text-[#7A6A5C] hover:text-[#1D9BF0] hover:bg-[#1D9BF0]/10 transition-colors"
            data-testid="telegram-support-link"
          >
            <Send className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1D9BF0]" />
            <span>Teknik destek ve kullanım ile ilgili sorunlar için iletişime geçiniz.</span>
          </a>
        </div>
      </nav>

      {/* User Section */}
      <div className="border-t border-[#E6DCCF] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#E8D9C7] flex items-center justify-center flex-shrink-0">
            <span className="text-[#2E2620] font-medium">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-sm font-medium text-[#2E2620] truncate">
                {user?.full_name || 'Kullanıcı'}
              </p>
              {isSuperAdmin ? (
                <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#2E2620] text-white leading-none">
                  Süper Admin
                </span>
              ) : isAdmin ? (
                <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#6D7C3B]/15 text-[#6D7C3B] leading-none">
                  Admin
                </span>
              ) : (
                <PackageBadge pkg={currentPkg} size="sm" />
              )}
            </div>
            <p className="text-xs text-[#7A6A5C] truncate">{user?.email}</p>
          </div>
        </div>

        {/* Abonelik süresi göstergesi */}
        {!isAdmin && daysLeft !== null && (
          <div
            className={`flex items-center gap-1.5 text-xs rounded-lg px-3 py-2 mb-2 border ${
              daysLeft <= 3
                ? 'bg-[#B04832]/10 border-[#B04832]/20 text-[#B04832]'
                : daysLeft <= 7
                  ? 'bg-[#C86F4A]/10 border-[#C86F4A]/20 text-[#C86F4A]'
                  : 'bg-[#6D7C3B]/10 border-[#6D7C3B]/20 text-[#6D7C3B]'
            }`}
          >
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="font-medium">
              {daysLeft <= 0
                ? 'Abonelik süreniz doldu'
                : `Aboneliğinizin bitmesine ${daysLeft} gün kaldı`}
            </span>
          </div>
        )}

        {!isAdmin && (
          <button
            onClick={() => { setMobileOpen(false); setPackageModalOpen(true); }}
            className="w-full flex items-center justify-between text-xs text-[#7A6A5C] hover:text-[#C86F4A] bg-[#F6F1EA] hover:bg-[#F0E8DC] border border-[#E6DCCF] rounded-lg px-3 py-2 transition-colors mb-2"
            data-testid="package-modal-btn"
          >
            <span>Paketimi Değiştir</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}

        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => { setMobileOpen(false); navigate('/admin/profile'); }}
            className="w-full justify-start border-[#E6DCCF] text-[#2E2620] hover:bg-[#F6F1EA] mb-2"
            data-testid="admin-profile-btn"
          >
            <UserCog className="w-4 h-4 mr-2" />
            Kişisel Bilgiler
          </Button>
        )}

        <Button
          variant="ghost"
          onClick={logout}
          className="w-full justify-start text-[#7A6A5C] hover:text-[#B04832] hover:bg-[#B04832]/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Çıkış Yap
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E6DCCF] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#C86F4A] flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-[#2E2620] font-['Playfair_Display']">
              BIST Analiz
            </span>
          </div>

          {/* Always-colored mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex items-center gap-1.5 bg-[#C86F4A] hover:bg-[#B05D3A] active:bg-[#9A4D2A] text-white rounded-xl px-3 py-2 transition-colors shadow-sm"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen
              ? <><X className="w-4 h-4" /><span className="text-sm font-semibold">Kapat</span></>
              : <><Menu className="w-4 h-4" /><span className="text-sm font-semibold">Menü</span></>
            }
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 z-50 h-full w-72 bg-white transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <NavContent />
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-[#E6DCCF]">
        <NavContent />
      </aside>

      {/* Package upgrade modal */}
      <PackageModal
        open={packageModalOpen}
        onOpenChange={setPackageModalOpen}
        currentPkg={currentPkg}
      />

      {/* Platinum upgrade gate dialog */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm p-0 overflow-hidden border-0 rounded-2xl">
          {/* Dark header */}
          <div className="bg-[#2E2620] px-6 pt-7 pb-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#8f969e]/15 border border-[#8f969e]/30 flex items-center justify-center mx-auto mb-3">
              <Crown className="w-7 h-7 text-[#8f969e]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">
              Platinum'a Özel İçerik
            </h2>
            <p className="text-[#A89F91] text-sm mt-2 leading-relaxed">
              Bu içeriğe erişmek için <span className="text-white font-semibold">Platinum</span> pakete
              sahip olmanız gerekmektedir.
            </p>
          </div>

          {/* Feature list */}
          <div className="bg-[#F6F1EA] px-5 pt-4 pb-2 space-y-2">
            {[
              { icon: Crown,    text: 'Simons Analizi — aylık özel hisse tavsiyeleri' },
              { icon: BarChart2, text: 'BIST Analizi — profesyonel piyasa analizi' },
              { icon: Infinity,  text: 'Sınırsız sorgu hakkı' },
              { icon: Zap,       text: 'Öncelikli tarama kuyruğu' },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-[#E6DCCF] rounded-xl px-4 py-2.5">
                <Icon className="w-4 h-4 text-[#8f969e] flex-shrink-0" />
                <p className="text-sm text-[#2E2620]">{text}</p>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="bg-[#F6F1EA] px-5 pb-6 pt-3 flex flex-col gap-2">
            <Button
              onClick={() => { setUpgradeModalOpen(false); setPackageModalOpen(true); }}
              className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Paketi Yükselt
            </Button>
            <button
              onClick={() => setUpgradeModalOpen(false)}
              className="text-xs text-[#7A6A5C] hover:text-[#2E2620] transition-colors py-1"
            >
              Şimdi değil
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sidebar;
